import { useEffect, useRef, useState } from "react";
import { Icon } from "../Icon";
import { HorizontalSpacer } from "../utils/Utils";
import { buildClasses } from "../utils/buildClasses";
import { RYE_CARD_SURFACE } from "./RyeCard";
import { isDragLeavingContainer } from "../utils/isDragLeavingContainer";

export interface ItemIndices {
  groupIndex: number;
  itemIndex: number;
}

export interface ItemsGroup {
  id: string;
  value: JSX.Element;
  items: { id: string; value: JSX.Element }[];
}

// NOTE: We do the re-ordering in the onDrop event instead of
// the item being dragged's onDragEnd event because onDragEnd doesn't fire
// until the dragged item is actually back where it belongs, not right when
// the dragged item is released by the mouse. We want to re-order things
// right when the mouse is released so the animation of the dragged item
// jumps into it's *new* position when released, not its old one.
// Leaving the list clears the insertion target; transitions between children
// do not count as leaving the list.
export function RyeDraggableGroupedList({
  inputGroupedItems,
  onGroupOrderUpdate,
  onItemOrderUpdate,
  collapsedGroupSpacerHeight = 30,
  collapsedItemSpacerHeight = 10,
  showItemDividingLines = true,
  backgroundColor = "#FFFFFF",
  // surface-300, the warm tint Home cards use.
  groupColor = "#f9f8f5",
  itemColor = "#f9f8f5",
  groupClass = "", // For https://tailwindcss.com/docs/hover-focus-and-other-states#styling-based-on-parent-state
}: {
  inputGroupedItems: ItemsGroup[];
  onGroupOrderUpdate: (
    newIdList: string[],
    id: string,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onItemOrderUpdate: (
    newGroupedItems: ItemsGroup[],
    id: string,
    oldIndices: ItemIndices,
    newIndices: ItemIndices,
  ) => void;
  collapsedGroupSpacerHeight?: number;
  collapsedItemSpacerHeight?: number;
  showItemDividingLines?: boolean;
  backgroundColor?: string;
  groupColor?: string;
  itemColor?: string;
  groupClass?: string;
}): JSX.Element {
  const [localGroupedItems, setLocalGroupedItems] =
    useState<ItemsGroup[]>(inputGroupedItems);
  // Null if no group is currently being dragged.
  const [groupDraggingIndex, setGroupDraggingIndex] = useState<number | null>(
    null,
  );
  // Null if no group is currently being dragged or the group being dragged
  // is outside the valid drop area (i.e. outside the whole list).
  const [groupTargetIndex, setGroupTargetIndex] = useState<number | null>(null);
  // Null if no individual item is currently being dragged.
  const [itemDraggingIndices, setItemDraggingIndices] =
    useState<ItemIndices | null>(null);
  // Null if no indivdual item is currently being dragged or the item being
  // dragged is outside the valid drop area (i.e. outside the whole list).
  const [itemTargetIndices, setItemTargetIndices] =
    useState<ItemIndices | null>(null);
  // When true, transition (i.e. animate) changes in the height of spacers.
  const [transitionSpacerHeight, setTransitionSpacerHeight] = useState(false);
  // See the comment on freezeListContainerHeightIfNecessary()
  const [frozenListContainerHeight, setFrozenListContainerHeight] = useState<
    number | null
  >(null);
  const draggingElementHeight = useRef<number>(0);
  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Synchronize any updates made in the outside world to the local groups/items
    setLocalGroupedItems(inputGroupedItems);
  }, [inputGroupedItems]);

  function efficientlySetGroupTargetIndex(newIndex: number | null) {
    if (newIndex !== groupTargetIndex) {
      setGroupTargetIndex(newIndex);
    }
  }

  function efficientlySetTransitionSpacerHeight(newVal: boolean) {
    if (newVal !== transitionSpacerHeight) {
      setTransitionSpacerHeight(newVal);
    }
  }

  function efficientlySetItemTargetIndices(newIndices: ItemIndices | null) {
    if (itemIndicesAreEqual(newIndices, itemTargetIndices)) {
      return;
    }
    if (newIndices === null) {
      setItemTargetIndices(null);
      return;
    }
    setItemTargetIndices({ ...newIndices });
  }

  // When groups are large, we shrink the spacer placeholder to make it
  // less jarring/cumbersome to preview group re-ordering. However, this
  // can shrink the overall height of the list, which can automatically
  // scroll upward at the beginning of a drag. This is disorienting, so to
  // prevent this we "freeze" the height of the entire list right at the
  // beginning of a group drag.
  function freezeListContainerHeightIfNecessary() {
    if (
      frozenListContainerHeight === null &&
      groupDraggingIndex !== null &&
      transitionSpacerHeight &&
      listContainerRef.current
    ) {
      setFrozenListContainerHeight(
        listContainerRef.current.getBoundingClientRect().height,
      );
    } else if (
      frozenListContainerHeight !== null &&
      groupDraggingIndex === null
    ) {
      setFrozenListContainerHeight(null);
    }
  }

  // Makes a copy and does not modify the input array.
  function arrayMove<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
    let newArr = [...arr];
    var element = newArr[fromIndex];
    newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, element);
    return newArr;
  }

  // Makes a copy and does not modify the input array.
  function arrayAddAt<T>(arr: T[], insertionIndex: number, item: T): T[] {
    let newArr = [...arr];
    newArr.splice(insertionIndex, 0, item);
    return newArr;
  }

  // Makes a copy and does not modify the input array.
  function arrayRemoveFrom<T>(arr: T[], removalIndex: number): T[] {
    let newArr = [...arr];
    newArr.splice(removalIndex, 1);
    return newArr;
  }

  function updateOrder() {
    if (groupDraggingIndex !== null) {
      if (groupTargetIndex === null) {
        return;
      }

      setLocalGroupedItems((prevState) => {
        const newList = arrayMove(
          prevState,
          groupDraggingIndex,
          groupTargetIndex,
        );
        onGroupOrderUpdate(
          newList.map((group) => group.id),
          prevState[groupDraggingIndex].id,
          groupDraggingIndex,
          groupTargetIndex,
        );
        return newList;
      });

      return;
    }

    if (itemDraggingIndices !== null) {
      if (itemTargetIndices === null) {
        return;
      }

      if (itemDraggingIndices.groupIndex === itemTargetIndices.groupIndex) {
        setLocalGroupedItems((prevState) => {
          const newGroupedItems = [...prevState];
          const newGroup = { ...prevState[itemTargetIndices.groupIndex] };
          newGroup.items = arrayMove(
            prevState[itemTargetIndices.groupIndex].items,
            itemDraggingIndices.itemIndex,
            itemTargetIndices.itemIndex,
          );
          newGroupedItems[itemTargetIndices.groupIndex] = newGroup;
          onItemOrderUpdate(
            newGroupedItems,
            prevState[itemDraggingIndices.groupIndex].items[
              itemDraggingIndices.itemIndex
            ].id,
            itemDraggingIndices,
            itemTargetIndices,
          );
          return newGroupedItems;
        });

        return;
      }

      setLocalGroupedItems((prevState) => {
        const newGroupedItems = [...prevState];
        const newFromGroup = { ...prevState[itemDraggingIndices.groupIndex] };
        const newTargetGroup = { ...prevState[itemTargetIndices.groupIndex] };
        newFromGroup.items = arrayRemoveFrom(
          prevState[itemDraggingIndices.groupIndex].items,
          itemDraggingIndices.itemIndex,
        );
        newTargetGroup.items = arrayAddAt(
          prevState[itemTargetIndices.groupIndex].items,
          itemTargetIndices.itemIndex,
          prevState[itemDraggingIndices.groupIndex].items[
            itemDraggingIndices.itemIndex
          ],
        );

        newGroupedItems[itemDraggingIndices.groupIndex] = newFromGroup;
        newGroupedItems[itemTargetIndices.groupIndex] = newTargetGroup;
        onItemOrderUpdate(
          newGroupedItems,
          prevState[itemDraggingIndices.groupIndex].items[
            itemDraggingIndices.itemIndex
          ].id,
          itemDraggingIndices,
          itemTargetIndices,
        );
        console.log(newGroupedItems);
        return newGroupedItems;
      });
    }
  }

  let draggableGroupedList: JSX.Element[] = [];

  draggableGroupedList.push(
    <GroupSpacer
      key={`group spacer ${0}`}
      index={0}
      groupDraggingIndex={groupDraggingIndex}
      groupTargetIndex={groupTargetIndex}
      collapsedHeight={collapsedGroupSpacerHeight}
      transitionSpacerHeight={transitionSpacerHeight}
      draggingElementHeight={draggingElementHeight}
      setGroupTargetIndex={efficientlySetGroupTargetIndex}
      setTransitionSpacerHeight={efficientlySetTransitionSpacerHeight}
    />,
  );

  localGroupedItems.forEach((group, index) => {
    draggableGroupedList.push(
      <Group
        key={group.id}
        group={group}
        index={index}
        groupDraggingIndex={groupDraggingIndex}
        itemDraggingIndices={itemDraggingIndices}
        setGroupDraggingIndex={setGroupDraggingIndex}
        setItemDraggingIndices={setItemDraggingIndices}
        groupColor={groupColor}
        itemColor={itemColor}
        groupTargetIndex={groupTargetIndex}
        itemTargetIndices={itemTargetIndices}
        collapsedItemSpacerHeight={collapsedItemSpacerHeight}
        transitionSpacerHeight={transitionSpacerHeight}
        draggingElementHeight={draggingElementHeight}
        setGroupTargetIndex={efficientlySetGroupTargetIndex}
        setItemTargetIndices={efficientlySetItemTargetIndices}
        showItemDividingLines={showItemDividingLines}
        setTransitionSpacerHeight={efficientlySetTransitionSpacerHeight}
        groupClass={groupClass}
      />,
    );
    if (groupDraggingIndex !== index) {
      // When dragging to get the spacing right we remove the spacer
      // whose index is 1 greater than the dragging index.
      draggableGroupedList.push(
        <GroupSpacer
          key={`group spacer ${index + 1}`}
          index={
            groupDraggingIndex !== null && index > groupDraggingIndex
              ? index
              : index + 1
          }
          groupDraggingIndex={groupDraggingIndex}
          groupTargetIndex={groupTargetIndex}
          collapsedHeight={collapsedGroupSpacerHeight}
          transitionSpacerHeight={transitionSpacerHeight}
          draggingElementHeight={draggingElementHeight}
          setGroupTargetIndex={efficientlySetGroupTargetIndex}
          setTransitionSpacerHeight={efficientlySetTransitionSpacerHeight}
        />,
      );
    }
  });

  freezeListContainerHeightIfNecessary();

  return (
    <div
      ref={listContainerRef}
      className={buildClasses("flow-root")}
      // Cancel the outer insertion spacers without removing their drop targets.
      style={{
        backgroundColor,
        marginTop: -collapsedGroupSpacerHeight,
        marginBottom:
          localGroupedItems.length > 0 ? -collapsedGroupSpacerHeight : 0,
        height: frozenListContainerHeight ?? undefined,
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragLeave={(e) => {
        if (isDragLeavingContainer(e)) {
          setGroupTargetIndex(null);
          setItemTargetIndices(null);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateOrder();
        setTransitionSpacerHeight(false);
        setGroupDraggingIndex(null);
        setItemDraggingIndices(null);
        setGroupTargetIndex(null);
        setItemTargetIndices(null);
      }}
    >
      {draggableGroupedList}
    </div>
  );
}

function Group(props: {
  group: {
    id: string;
    value: JSX.Element;
    items: { id: string; value: JSX.Element }[];
  };
  index: number;
  groupDraggingIndex: number | null;
  itemDraggingIndices: ItemIndices | null;
  setGroupDraggingIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setItemDraggingIndices: React.Dispatch<
    React.SetStateAction<ItemIndices | null>
  >;
  groupColor: string;
  itemColor: string;
  groupTargetIndex: number | null;
  itemTargetIndices: ItemIndices | null;
  collapsedItemSpacerHeight: number;
  transitionSpacerHeight: boolean;
  draggingElementHeight: React.MutableRefObject<number>;
  setGroupTargetIndex: (newIndex: number | null) => void;
  setItemTargetIndices: (newIndices: ItemIndices | null) => void;
  showItemDividingLines: boolean;
  setTransitionSpacerHeight: (newVal: boolean) => void;
  groupClass: string;
}): JSX.Element {
  const [draggable, setDraggable] = useState(false);
  const [dragging, setDragging] = useState(false);

  const divRef = useRef<HTMLDivElement>(null);

  let draggableList: JSX.Element[] = [];
  draggableList.push(
    <ItemSpacer
      key={"spacer 0"}
      indices={{ groupIndex: props.index, itemIndex: 0 }}
      itemDraggingIndices={props.itemDraggingIndices}
      targetIndices={props.itemTargetIndices}
      collapsedHeight={props.collapsedItemSpacerHeight}
      transitionSpacerHeight={props.transitionSpacerHeight}
      draggingElementHeight={props.draggingElementHeight}
      setTargetIndices={props.setItemTargetIndices}
      showDividingLines={props.showItemDividingLines}
      isFirstOrLastSpacer={true}
      setTransitionSpacerHeight={props.setTransitionSpacerHeight}
    />,
  );
  props.group.items.forEach((value, index) => {
    draggableList.push(
      <RyeDraggableListItem
        key={value.id}
        indices={{ groupIndex: props.index, itemIndex: index }}
        item={value}
        transitionSpacerHeight={props.transitionSpacerHeight}
        draggingElementHeight={props.draggingElementHeight}
        draggingIndices={props.itemDraggingIndices}
        setDraggingIndices={props.setItemDraggingIndices}
        setTargetIndices={props.setItemTargetIndices}
        itemColor={props.itemColor}
        setTransitionSpacerHeight={props.setTransitionSpacerHeight}
      />,
    );
    if (
      !itemIndicesAreEqual(props.itemDraggingIndices, {
        groupIndex: props.index,
        itemIndex: index,
      })
    ) {
      // When dragging to get the spacing right we remove the spacer
      // whose index is 1 greater than the dragging index.
      draggableList.push(
        <ItemSpacer
          key={`spacer ${index + 1}`}
          indices={
            props.itemDraggingIndices !== null &&
            props.itemDraggingIndices.groupIndex === props.index &&
            index > props.itemDraggingIndices.itemIndex
              ? { groupIndex: props.index, itemIndex: index }
              : { groupIndex: props.index, itemIndex: index + 1 }
          }
          itemDraggingIndices={props.itemDraggingIndices}
          targetIndices={props.itemTargetIndices}
          collapsedHeight={props.collapsedItemSpacerHeight}
          transitionSpacerHeight={props.transitionSpacerHeight}
          draggingElementHeight={props.draggingElementHeight}
          setTargetIndices={props.setItemTargetIndices}
          showDividingLines={props.showItemDividingLines}
          isFirstOrLastSpacer={
            props.itemDraggingIndices?.itemIndex ===
            props.group.items.length - 1
              ? index + 2 === props.group.items.length
              : index + 1 === props.group.items.length
          }
          setTransitionSpacerHeight={props.setTransitionSpacerHeight}
        />,
      );
    }
  });
  return (
    <div
      ref={divRef}
      draggable={draggable}
      className={buildClasses(
        { if: dragging, then: buildClasses("invisible", "hidden", "h-0") },
        RYE_CARD_SURFACE,
        "p-5",
        "isolate", // necessary hack that adds a stacking context for getting border radius to work on draggable element while dragging - see https://github.com/react-dnd/react-dnd/issues/788#issuecomment-367300464
        "relative",
        props.groupClass,
      )}
      style={{
        zIndex: 1000 - props.index,
      }}
      onDrag={() => {
        if (!dragging) {
          setDragging(true);
          props.setGroupDraggingIndex(props.index);
          props.setTransitionSpacerHeight(false);
          props.setGroupTargetIndex(props.index);
          props.draggingElementHeight.current =
            divRef.current?.getBoundingClientRect().height ?? 0;
        }
      }}
      onDragEnd={() => {
        setDragging(false);
        setDraggable(false);
        props.setGroupDraggingIndex(null);
        props.setGroupTargetIndex(null);
        props.setTransitionSpacerHeight(false);
      }}
      onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (props.groupDraggingIndex === null || divRef.current === null) {
          return;
        }

        const mousePositionY = e.clientY;
        const itemCenterPositionY =
          divRef.current.getBoundingClientRect().top +
          divRef.current.getBoundingClientRect().height / 2;

        if (
          mousePositionY < itemCenterPositionY &&
          props.groupDraggingIndex < props.index
        ) {
          props.setGroupTargetIndex(props.index - 1);
        }
        if (
          mousePositionY < itemCenterPositionY &&
          props.groupDraggingIndex > props.index
        ) {
          props.setGroupTargetIndex(props.index);
        }
        if (
          mousePositionY > itemCenterPositionY &&
          props.groupDraggingIndex < props.index
        ) {
          props.setGroupTargetIndex(props.index);
        }
        if (
          mousePositionY > itemCenterPositionY &&
          props.groupDraggingIndex > props.index
        ) {
          props.setGroupTargetIndex(props.index + 1);
        }
      }}
    >
      <div className={buildClasses("flex", "items-center", "py-2")}>
        <div
          className={buildClasses("flex", "items-center")}
          onMouseEnter={() => setDraggable(true)}
          onMouseLeave={() => setDraggable(false)}
        >
          <Icon
            name={"drag_indicator"}
            size={20}
            className={buildClasses("cursor-move")}
          />
        </div>
        <HorizontalSpacer width={20} />
        {props.group.value}
      </div>
      <div>{draggableList}</div>
    </div>
  );
}

function GroupSpacer(props: {
  index: number;
  groupDraggingIndex: number | null;
  groupTargetIndex: number | null;
  collapsedHeight: number;
  transitionSpacerHeight: boolean;
  draggingElementHeight: React.MutableRefObject<number>;
  setGroupTargetIndex: (newIndex: number | null) => void;
  setTransitionSpacerHeight: (newVal: boolean) => void;
}): JSX.Element {
  function isExpanded() {
    return props.index === props.groupTargetIndex;
  }

  let expandedHeight =
    props.draggingElementHeight.current + 2 * props.collapsedHeight;
  if (props.transitionSpacerHeight) {
    expandedHeight = Math.min(
      props.draggingElementHeight.current + 2 * props.collapsedHeight,
      100,
    );
  }

  return (
    <div
      style={{
        height: isExpanded() ? expandedHeight : props.collapsedHeight,
        paddingTop: isExpanded() ? props.collapsedHeight + 5 : 0,
        paddingBottom: isExpanded() ? props.collapsedHeight + 5 : 0,
        paddingLeft: isExpanded() ? 5 : 0,
        paddingRight: isExpanded() ? 5 : 0,
      }}
      className={buildClasses(
        {
          if: props.transitionSpacerHeight,
          then: buildClasses("transition-height", "duration-200"),
        },
        "box-border",
        "rounded-lg",
      )}
      onDragEnter={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (props.groupDraggingIndex !== null) {
          props.setGroupTargetIndex(props.index);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        props.setTransitionSpacerHeight(true);
      }}
      onDragLeave={() => {
        // Probably overkill to set transition spacer height here as well, but
        // just to be safe.
        props.setTransitionSpacerHeight(true);
      }}
    >
      {/* Shadowy rectangle showing drop zone */}
      <div
        className={buildClasses(
          {
            if: isExpanded(),
            then: buildClasses(
              "bg-surface-500",
              "shadow-draggable-list-target",
              "shadow-surface-500",
            ),
          },
          "w-full",
          "h-full",
          "rounded-lg",
        )}
      />
    </div>
  );
}

function itemIndicesAreEqual(
  indicesA: ItemIndices | null,
  indicesB: ItemIndices | null,
): boolean {
  if (indicesA === null && indicesB === null) {
    return true;
  }
  if (indicesA === null || indicesB === null) {
    return false;
  }
  return (
    indicesA.groupIndex === indicesB.groupIndex &&
    indicesA.itemIndex === indicesB.itemIndex
  );
}

function RyeDraggableListItem(props: {
  item: { id: string; value: JSX.Element };
  indices: ItemIndices;
  transitionSpacerHeight: boolean;
  draggingElementHeight: React.MutableRefObject<number>;
  draggingIndices: ItemIndices | null;
  setDraggingIndices: React.Dispatch<React.SetStateAction<ItemIndices | null>>;
  setTargetIndices: (newIndex: ItemIndices | null) => void;
  itemColor: string;
  setTransitionSpacerHeight: (newVal: boolean) => void;
}): JSX.Element {
  const [draggable, setDraggable] = useState(false);
  const [dragging, setDragging] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={divRef}
      draggable={draggable}
      className={buildClasses(
        { if: dragging, then: buildClasses("invisible", "hidden", "h-0") },
        "flex",
        "items-center",
        // Matches the group card, so items read as rows within it.
        "bg-white",
        "px-5",
        "rounded-lg",
        "isolate", // necessary hack that adds a stacking context stacking contextfor getting border radius to work on draggable element while dragging - see https://github.com/react-dnd/react-dnd/issues/788#issuecomment-367300464
        "relative",
      )}
      style={{
        zIndex: 1000 - props.indices.itemIndex,
      }}
      onDrag={(e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!dragging) {
          setDragging(true);
          props.setDraggingIndices(props.indices);
          props.setTransitionSpacerHeight(false);
          props.setTargetIndices(props.indices);
          props.draggingElementHeight.current =
            divRef.current?.getBoundingClientRect().height ?? 0;
        }
      }}
      onDragEnd={(e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setDragging(false);
        setDraggable(false);
        props.setDraggingIndices(null);
        props.setTargetIndices(null);
        props.setTransitionSpacerHeight(false);
      }}
      onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        // See https://stackoverflow.com/questions/50230048/react-ondrop-is-not-firing
        // for why we prevent default
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (props.draggingIndices === null) {
          return;
        }
        e.stopPropagation();
        if (props.draggingIndices === null || divRef.current === null) {
          return;
        }

        const mousePositionY = e.clientY;
        const itemCenterPositionY =
          divRef.current.getBoundingClientRect().top +
          divRef.current.getBoundingClientRect().height / 2;

        if (props.draggingIndices.groupIndex !== props.indices.groupIndex) {
          if (mousePositionY < itemCenterPositionY) {
            props.setTargetIndices({
              groupIndex: props.indices.groupIndex,
              itemIndex: props.indices.itemIndex,
            });
          }
          if (mousePositionY > itemCenterPositionY) {
            props.setTargetIndices({
              groupIndex: props.indices.groupIndex,
              itemIndex: props.indices.itemIndex + 1,
            });
          }
          return;
        }

        if (
          mousePositionY < itemCenterPositionY &&
          props.draggingIndices.itemIndex < props.indices.itemIndex
        ) {
          props.setTargetIndices({
            groupIndex: props.indices.groupIndex,
            itemIndex: props.indices.itemIndex - 1,
          });
        }
        if (
          mousePositionY < itemCenterPositionY &&
          props.draggingIndices.itemIndex > props.indices.itemIndex
        ) {
          props.setTargetIndices({
            groupIndex: props.indices.groupIndex,
            itemIndex: props.indices.itemIndex,
          });
        }
        if (
          mousePositionY > itemCenterPositionY &&
          props.draggingIndices.itemIndex < props.indices.itemIndex
        ) {
          props.setTargetIndices({
            groupIndex: props.indices.groupIndex,
            itemIndex: props.indices.itemIndex,
          });
        }
        if (
          mousePositionY > itemCenterPositionY &&
          props.draggingIndices.itemIndex > props.indices.itemIndex
        ) {
          props.setTargetIndices({
            groupIndex: props.indices.groupIndex,
            itemIndex: props.indices.itemIndex + 1,
          });
        }
      }}
    >
      <div
        className={buildClasses("flex", "items-center")}
        onMouseEnter={() => setDraggable(true)}
        onMouseLeave={() => setDraggable(false)}
      >
        <Icon
          name={"drag_indicator"}
          size={20}
          className={buildClasses("cursor-move")}
        />
      </div>
      <HorizontalSpacer width={20} />
      {props.item.value}
    </div>
  );
}

function ItemSpacer(props: {
  indices: ItemIndices;
  itemDraggingIndices: ItemIndices | null;
  targetIndices: ItemIndices | null;
  collapsedHeight: number;
  transitionSpacerHeight: boolean;
  draggingElementHeight: React.MutableRefObject<number>;
  setTargetIndices: (newIndex: ItemIndices | null) => void;
  showDividingLines: boolean;
  isFirstOrLastSpacer: boolean;
  setTransitionSpacerHeight: (newVal: boolean) => void;
}): JSX.Element {
  function isExpanded() {
    return itemIndicesAreEqual(props.targetIndices, props.indices);
  }

  return (
    <div
      style={{
        height: isExpanded()
          ? props.draggingElementHeight.current - 10
          : props.collapsedHeight,
        marginTop: isExpanded() ? props.collapsedHeight + 5 : 0,
        marginBottom: isExpanded() ? props.collapsedHeight + 5 : 0,
        marginLeft: isExpanded() ? 5 : 0,
        marginRight: isExpanded() ? 5 : 0,
      }}
      className={buildClasses(
        {
          if: isExpanded(),
          then: buildClasses(
            "bg-surface-400",
            "shadow-draggable-list-target",
            "shadow-surface-400",
          ),
        },
        {
          if: props.transitionSpacerHeight,
          then: buildClasses("transition-height"),
        },
        "box-border",
        "flex",
        "items-center",
        "justify-center",
        "rounded-lg",
      )}
      onDragEnter={(e) => {
        if (props.itemDraggingIndices === null) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (props.itemDraggingIndices !== null) {
          props.setTargetIndices(props.indices);
        }
        e.dataTransfer.dropEffect = "move";
      }}
      onDragOver={(e) => {
        if (props.itemDraggingIndices === null) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        props.setTransitionSpacerHeight(true);
      }}
      onDragLeave={(e) => {
        if (props.itemDraggingIndices === null) {
          return;
        }
        e.stopPropagation();
        props.setTransitionSpacerHeight(true);
      }}
    >
      {props.showDividingLines &&
      !props.isFirstOrLastSpacer &&
      !isExpanded() ? (
        <div className={buildClasses("w-full", "h-px", "bg-surface-500")}></div>
      ) : null}
    </div>
  );
}
